import { DOCUMENT } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  Inject,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { PAYWALL_MASTER_ROUTE } from '../../../main/main.data';
import { IIcon } from '../../../shared/components/icon/icon.interface';
import { IconSizesEnum } from '../../../shared/components/icon/icons-sizes.enum';
import { URLS } from '../../../shared/constants/urls.constant';
import { NavigationService } from 'i2g-ng-utils';
import { TrackingScreenNamesEnum } from '../../../tracking/tracking.data';
import {
  filter,
  map,
  shareReplay,
  takeUntil,
  tap,
  withLatestFrom,
} from 'rxjs/operators';
import {
  IAccountDataResolveData,
  RESOLVE_KEY as accountDataResolveKey,
} from '../../../account-data/account-data.data';
import { ActivatedRoute } from '@angular/router';
import { DialogService } from '../../../dialog/dialog.service';
import { noop, Observable, Subject } from 'rxjs';
import { ErrorDialogBuilder } from '../../../dialog/components/error-dialog/error-dialog.builder';
import {
  I2gNgTrackingActionBuilder,
  ITrackingAction,
  ITrackingObject,
} from 'i2g-ng-tracking';
import { Toggles } from '../../../features-data/sub-models/toggles.model';
import { FeedbackScreenEnum } from '../../../feedback-data/feedback-data.data';
import { DIALOG_TYPE as cancelSubscriptionFeedbackDialog } from '../../../dialog/components/cancel-subscription-feedback-dialog/cancel-subscription-feedback-dialog.component';
import { OfferTypesEnum } from '../../../pricing-data/pricing-data.data';
import { DIALOG_TYPE as cancelSubscriptionOfferDialog } from '../../../dialog/components/cancel-subscription-offer-dialog/cancel-subscription-offer-dialog.component';
import {
  BILLING_MANAGE_RENEW_DISABLE_ROUTE,
  BILLING_MANAGE_RENEW_ROUTE,
} from '../../../billing-data/billing-data.data';
import { PricingDataStore } from '../../../pricing-data/pricing-data.store';
import { FeedbackDataStore } from '../../../feedback-data/feedback-data.store';
import { FeedbackDataDispatcher } from '../../../feedback-data/feedback-data.dispatcher';
import {
  CurrentPlan,
  SubscriptionTypeEnum,
} from '../../../features-data/sub-models/current-plan.model';
import { FeaturesDataStore } from '../../../features-data/features-data.store';
import { PricingDataDispatcher } from '../../../pricing-data/pricing-data.dispatcher';
import { AuthDataDispatcher } from '../../../auth-data/auth-data.dispatcher';
import { TrackingDispatcher } from '../../../tracking/tracking.dispatcher';
import { TrackingObjectBuilder } from '../../../tracking/tracking-object.builder';
import { VerifyAccountDispatcher } from '../../verify-account.dispatcher';
import { I2gDialogService } from 'i2g-ng-ui';
import { ChangeEmailDialogComponent } from '../../../shared/components/change-email-dialog/change-email-dialog.component';

@Component({
  selector: 'app-account-blocked',
  templateUrl: './account-blocked.component.html',
  styleUrls: ['./account-blocked.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  viewProviders: [ErrorDialogBuilder],
})
export class AccountBlockedComponent
  implements OnInit, AfterViewInit, OnDestroy {
  private _destroyed: Subject<boolean> = new Subject<boolean>();
  private _accountId: string;

  loggingOut: boolean;
  invoice2goLogo: IIcon;
  accountBlockIcon: IIcon;
  userEmail: string;
  isOwner: boolean;
  invoice2goUrl: string;
  isCancelableToggleEnabled$: Observable<boolean>;
  isCancelable$: Observable<boolean>;
  currentPlan$: Observable<CurrentPlan>;
  isSubscribedOnWeb$: Observable<boolean>;
  isSubscribedOnApple$: Observable<boolean>;
  isSubscribedOnGoogle$: Observable<boolean>;

  trackingScreenName: TrackingScreenNamesEnum;
  trackingObject: ITrackingObject;
  cancelSubscriptionTrackingAction: ITrackingAction;
  logOutTrackingAction: ITrackingAction;
  contactSupportEmailTrackingAction: ITrackingAction;
  resendEmailTrackingAction: ITrackingAction;
  changeEmailTrackingAction: ITrackingAction;

  constructor(
    private _dialogService: DialogService,
    private _i2gDialogService: I2gDialogService,
    private _featuresDataStore: FeaturesDataStore,
    private _navigationService: NavigationService,
    private _pricingDataDispatcher: PricingDataDispatcher,
    private _pricingDataStore: PricingDataStore,
    private _route: ActivatedRoute,
    private _trackingActionBuilder: I2gNgTrackingActionBuilder,
    private _trackingDispatcher: TrackingDispatcher,
    private _trackingObjectBuilder: TrackingObjectBuilder,
    private _feedbackDataStore: FeedbackDataStore,
    private _feedbackDataDispatcher: FeedbackDataDispatcher,
    private _authDataDispatcher: AuthDataDispatcher,
    private _verifyAccountDispatcher: VerifyAccountDispatcher,
    @Inject(DOCUMENT) private _document: Document
  ) {}

  ngOnInit() {
    this.loggingOut = false;

    const routeData = this._route.snapshot.data;
    const accountData: IAccountDataResolveData =
      routeData[accountDataResolveKey];

    this.isOwner = accountData.isOwner;
    this.userEmail = accountData.user.email;
    this._accountId = accountData.account.accountId;

    this.invoice2goUrl = URLS.INVOICE2GO;

    this.invoice2goLogo = {
      name: 'invoice2go-logo-white',
      size: IconSizesEnum.custom,
    };

    this.accountBlockIcon = {
      name: 'account-block',
      size: IconSizesEnum.custom,
    };

    // tracking
    this.trackingScreenName = TrackingScreenNamesEnum.email_verification_lock;
    this.trackingObject = this._trackingObjectBuilder.buildEmailVerificationLock();
    this.cancelSubscriptionTrackingAction = this._trackingActionBuilder.buildButtonTapped(
      'cancel_subscription',
      this.trackingScreenName
    );
    this.logOutTrackingAction = this._trackingActionBuilder.buildButtonTapped(
      'log_out',
      this.trackingScreenName
    );
    this.contactSupportEmailTrackingAction = this._trackingActionBuilder.buildButtonTapped(
      'email_support',
      this.trackingScreenName
    );
    this.resendEmailTrackingAction = this._trackingActionBuilder.buildButtonTapped(
      'resend_verification_email',
      this.trackingScreenName
    );
    this.changeEmailTrackingAction = this._trackingActionBuilder.buildButtonTapped(
      'change_email',
      this.trackingScreenName
    );
    const pagePresented = this._trackingActionBuilder.buildPresented(
      this.trackingScreenName
    );
    this._trackingDispatcher.trackEvent(pagePresented, this.trackingObject);

    this.isCancelableToggleEnabled$ = this._featuresDataStore.featureToggles$.pipe(
      map((toggles: Toggles) =>
        toggles.isAllAccess(Toggles.KEYS.IN_APP_CANCELLATION)
      ),
      shareReplay(1)
    );
    this.currentPlan$ = this._featuresDataStore.currentPlan$.pipe(
      filter((plan) => !!plan),
      shareReplay(1)
    );
    this.isSubscribedOnWeb$ = this.currentPlan$.pipe(
      map((currentPlan: CurrentPlan) =>
        [
          SubscriptionTypeEnum.invoice2go_managed,
          SubscriptionTypeEnum.recurring_braintree,
        ].includes(currentPlan.subscriptionType)
      )
    );

    this.isSubscribedOnApple$ = this.currentPlan$.pipe(
      map(
        (currentPlan: CurrentPlan) =>
          currentPlan.subscriptionType === SubscriptionTypeEnum.recurring_apple
      )
    );
    this.isSubscribedOnGoogle$ = this.currentPlan$.pipe(
      map(
        (currentPlan: CurrentPlan) =>
          currentPlan.subscriptionType === SubscriptionTypeEnum.recurring_google
      )
    );

    this.isCancelable$ = this.isCancelableToggleEnabled$.pipe(
      withLatestFrom(this.isSubscribedOnWeb$),
      map(([isCancelableToggleEnabled, isSubscribedOnWeb]: boolean[]) => {
        return isCancelableToggleEnabled && isSubscribedOnWeb;
      }),
      shareReplay(1)
    );
  }

  ngAfterViewInit() {
    this._document.body.classList.add('loaded');
  }

  onChangeEmailClicked() {
    this._trackingDispatcher.trackEvent(
      this.trackingObject,
      this.changeEmailTrackingAction
    );

    this._i2gDialogService.openDialog(ChangeEmailDialogComponent, {
      trackingScreenName: this.trackingScreenName,
    });
  }

  onResendEmailClicked() {
    this._trackingDispatcher.trackEvent(
      this.trackingObject,
      this.resendEmailTrackingAction
    );
    this._verifyAccountDispatcher.resendVerificationEmail();
  }

  onCancelSubscription() {
    this._trackingDispatcher.trackEvent(
      this.trackingObject,
      this.cancelSubscriptionTrackingAction
    );

    // show feedback dialog if it exists; else show cancellation page
    this._feedbackDataStore
      .getScreenById(FeedbackScreenEnum.cancel_subscription)
      .pipe(
        withLatestFrom(
          this._feedbackDataStore.getFeedbackHiddenForFeatureNameForAccount(
            FeedbackScreenEnum.cancel_subscription,
            this._accountId
          )
        ),
        takeUntil(this._destroyed)
      )
      .subscribe(([screen, feedbackCardHidden]: [any, boolean]) => {
        if (!feedbackCardHidden && screen) {
          return this._dialogService.openDialog({
            type: cancelSubscriptionFeedbackDialog,
          });
        }

        // fetch offer canvas
        this._pricingDataDispatcher.fetchOffer(
          OfferTypesEnum.cancellation_winback
        );

        this._presentCancellationOffer();
      });
  }

  dismissCancelSubscriptionFeedback() {
    this._dialogService.closeDialog({ type: cancelSubscriptionFeedbackDialog });
  }

  onCancelSubscriptionFeedbackSubmit() {
    // hide survey for 28 days
    this._feedbackDataDispatcher.hideFeedbackCardForAccount(
      FeedbackScreenEnum.cancel_subscription,
      this._accountId
    );
    this._dialogService.closeDialog({ type: cancelSubscriptionFeedbackDialog });

    this._presentCancellationOffer();
  }

  dismissCancelSubscriptionOffer() {
    this._dialogService.closeDialog({ type: cancelSubscriptionOfferDialog });
  }

  private _presentCancellationOffer() {
    this._dialogService.openDialog({ type: cancelSubscriptionOfferDialog });

    this._pricingDataStore
      .getOfferByType(OfferTypesEnum.cancellation_winback)
      .pipe(
        takeUntil(this._destroyed),
        tap((offer) => {
          if (offer === false) {
            this._dialogService.closeDialog({
              type: cancelSubscriptionOfferDialog,
            });

            this._navigationService.goTo([
              PAYWALL_MASTER_ROUTE,
              BILLING_MANAGE_RENEW_ROUTE,
              BILLING_MANAGE_RENEW_DISABLE_ROUTE,
            ]);
          }
        })
      )
      .subscribe(noop, noop);
  }

  onLogOutClicked() {
    this._trackingDispatcher.trackEvent(
      this.trackingObject,
      this.logOutTrackingAction
    );

    this.loggingOut = true;

    this._authDataDispatcher.logout();
  }

  onEmailSupportClicked() {
    this._trackingDispatcher.trackEvent(
      this.trackingObject,
      this.contactSupportEmailTrackingAction
    );
  }

  ngOnDestroy() {
    this._i2gDialogService.closeDialog();
    this._dialogService.closeDialog({ type: cancelSubscriptionFeedbackDialog });

    this._destroyed.next(true);
    this._destroyed.unsubscribe();
  }
}
